// Compliance Scoring Service
// Calculates compliance scores with grade system and recommendations

export class ComplianceService {
  constructor() {
    // Scoring weights (for execution-based calculation)
    this.weights = {
      timing: 0.25,
      completeness: 0.35,
      accuracy: 0.25,
      evidence: 0.15
    };
  }

  /**
   * Calculate compliance from TASK data (source of truth).
   * Task stores protocol.complianceScore per protocol, computed from step completion:
   * - skipped = 0, completed_with_errors = 0.6, completed = 1 (or 0.85 if late)
   * Use this when task has completed protocols.
   */
  calculateFromTask(task) {
    const protocols = task?.protocols || [];
    const scores = protocols
      .filter(p => p.complianceScore != null)
      .map(p => p.complianceScore);
    if (scores.length === 0) return null;

    const overall = Math.round(
      scores.reduce((a, b) => a + b, 0) / scores.length
    );

    // Derive breakdown from task steps (completeness proxy)
    let completeness = 100;
    let accuracy = 100;
    let totalActions = 0;
    let completedActions = 0;
    for (const p of protocols) {
      for (const s of p.steps || []) {
        const actions = s.actions || [];
        totalActions += actions.length || 1;
        if (s.isSkipped) {
          completedActions += 0;
        } else if (s.completedWithErrors) {
          const done = (actions.filter(a => a.completed === true).length);
          completedActions += done;
        } else if (s.isComplete) {
          completedActions += actions.length || 1;
        }
      }
    }
    if (totalActions > 0) {
      completeness = Math.round((completedActions / totalActions) * 100);
      accuracy = completeness; // same proxy
    }

    return {
      overall_score: overall,
      breakdown: {
        timing_score: 100, // task doesn't store timing breakdown; execution can override
        completeness_score: completeness,
        accuracy_score: accuracy,
        evidence_score: 0 // task doesn't store evidence
      },
      grade: this.getGrade(overall / 100),
      recommendations: this.getRecommendations(
        1, // timing
        completeness / 100,
        accuracy / 100,
        0   // evidence - task has none
      )
    };
  }
  
  /**
   * Calculate compliance score for task execution (Phase 3).
   * Uses execution.steps_completed with actions_completed.
   * Prefer calculateFromTask(task) when task has protocol compliance - it reflects actual step completion.
   */
  async calculateComplianceScore(taskExecution) {
    try {
      const timing = await this.calculateTimingScore(taskExecution);
      const completeness = await this.calculateCompletenessScore(taskExecution);
      const accuracy = await this.calculateAccuracyScore(taskExecution);
      const evidence = await this.calculateEvidenceScore(taskExecution);
      
      const overall = (
        timing * this.weights.timing +
        completeness * this.weights.completeness +
        accuracy * this.weights.accuracy +
        evidence * this.weights.evidence
      ) * 100;
      
      return {
        overall_score: Math.round(overall),
        breakdown: {
          timing_score: Math.round(timing * 100),
          completeness_score: Math.round(completeness * 100),
          accuracy_score: Math.round(accuracy * 100),
          evidence_score: Math.round(evidence * 100)
        },
        grade: this.getGrade(overall / 100),
        recommendations: this.getRecommendations(timing, completeness, accuracy, evidence)
      };
    } catch (error) {
      console.error('Error calculating compliance score:', error);
      throw error;
    }
  }
  
  /**
   * Calculate timing score (0.0-1.0)
   */
  calculateTimingScore(taskExecution) {
    const stepsCompleted = taskExecution.steps_completed || [];
    
    if (stepsCompleted.length === 0) return 0;
    
    const actionTimingScores = [];
    
    // Calculate timing score for each action
    stepsCompleted.forEach(step => {
      const actionsCompleted = step.actions_completed || [];
      
      actionsCompleted.forEach(action => {
        if (action.duration_seconds && action.expected_duration_seconds) {
          const duration = action.duration_seconds;
          const expectedDuration = action.expected_duration_seconds;
          const minDuration = action.min_duration_seconds || expectedDuration * 0.5;
          const maxDuration = action.max_duration_seconds || expectedDuration * 1.5;
          
          let score = 1.0;
          
          if (duration < minDuration) {
            score = 0.5; // Too quick
          } else if (duration > maxDuration) {
            score = 0.7; // Too slow
          }
          
          actionTimingScores.push(score);
        }
      });
    });
    
    // If no action timing data, use step timing
    if (actionTimingScores.length === 0) {
      stepsCompleted.forEach(step => {
        if (step.duration_seconds && step.expected_duration_seconds) {
          const duration = step.duration_seconds;
          const expectedDuration = step.expected_duration_seconds;
          const minDuration = expectedDuration * 0.7;
          const maxDuration = expectedDuration * 1.3;
          
          let score = 1.0;
          
          if (duration < minDuration) {
            score = 0.5;
          } else if (duration > maxDuration) {
            score = 0.7;
          }
          
          actionTimingScores.push(score);
        }
      });
    }
    
    if (actionTimingScores.length === 0) return 1.0; // No timing data, assume perfect
    
    // Average all timing scores
    const averageScore = actionTimingScores.reduce((sum, score) => sum + score, 0) / actionTimingScores.length;
    return averageScore;
  }
  
  /**
   * Calculate completeness score (0.0-1.0)
   */
  calculateCompletenessScore(taskExecution) {
    // This would require knowing total steps/actions from protocol
    // For now, calculate based on steps_completed vs estimated
    
    const stepsCompleted = taskExecution.steps_completed || [];
    const totalSteps = taskExecution.total_steps || stepsCompleted.length;
    
    if (totalSteps === 0) return 0;
    
    const completedSteps = stepsCompleted.filter(s => s.validation_passed !== false).length;
    const completedActions = stepsCompleted.reduce((count, step) => {
      return count + (step.actions_completed || []).filter(a => a.validation_passed !== false).length;
    }, 0);
    
    const totalActions = stepsCompleted.reduce((count, step) => {
      return count + (step.actions_completed || []).length;
    }, 0);
    
    const stepsRatio = completedSteps / totalSteps;
    const actionsRatio = totalActions > 0 ? completedActions / totalActions : 1.0;
    
    // Check evidence completeness
    const requiredEvidenceCount = stepsCompleted.reduce((count, step) => {
      return count + (step.actions_completed || []).filter(a => 
        a.photo_urls?.length > 0 || a.signature_url
      ).length;
    }, 0);
    
    const evidenceRatio = totalActions > 0 ? requiredEvidenceCount / totalActions : 1.0;
    
    // Weighted average: 40% steps, 40% actions, 20% evidence
    const completenessScore = (
      stepsRatio * 0.4 +
      actionsRatio * 0.4 +
      evidenceRatio * 0.2
    );
    
    return completenessScore;
  }
  
  /**
   * Calculate accuracy score (0.0-1.0)
   */
  calculateAccuracyScore(taskExecution) {
    const stepsCompleted = taskExecution.steps_completed || [];
    
    if (stepsCompleted.length === 0) return 0;
    
    // Check sequence accuracy
    let correctSequenceSteps = 0;
    stepsCompleted.forEach((step, index) => {
      // Check if step is in correct order
      if (step.step_number === index + 1) {
        correctSequenceSteps++;
      }
    });
    
    const sequenceRatio = stepsCompleted.length > 0 ? correctSequenceSteps / stepsCompleted.length : 1.0;
    
    // Check validation passed
    const validationPassedActions = stepsCompleted.reduce((count, step) => {
      return count + (step.actions_completed || []).filter(a => a.validation_passed !== false).length;
    }, 0);
    
    const totalActions = stepsCompleted.reduce((count, step) => {
      return count + (step.actions_completed || []).length;
    }, 0);
    
    const validationRatio = totalActions > 0 ? validationPassedActions / totalActions : 1.0;
    
    // Weighted average: 50% sequence, 50% validation
    const accuracyScore = (
      sequenceRatio * 0.5 +
      validationRatio * 0.5
    );
    
    return accuracyScore;
  }
  
  /**
   * Calculate evidence score (0.0-1.0)
   */
  calculateEvidenceScore(taskExecution) {
    const stepsCompleted = taskExecution.steps_completed || [];
    
    if (stepsCompleted.length === 0) return 0;
    
    let photoQualityScore = 0;
    let signatureCompletenessScore = 0;
    let notesCompletenessScore = 0;
    let evidenceCount = 0;
    
    stepsCompleted.forEach(step => {
      const actionsCompleted = step.actions_completed || [];
      
      actionsCompleted.forEach(action => {
        if (action.photo_urls?.length > 0 || action.signature_url) {
          evidenceCount++;
          
          // Photo quality (simplified - would need image analysis)
          if (action.photo_urls?.length > 0) {
            photoQualityScore += 1.0; // Assume good quality if present
          }
          
          // Signature completeness
          if (action.signature_url) {
            signatureCompletenessScore += 1.0;
          }
          
          // Notes completeness
          if (action.notes && action.notes.trim().length > 0) {
            notesCompletenessScore += 1.0;
          }
        }
      });
    });
    
    if (evidenceCount === 0) return 0;
    
    const photoQuality = photoQualityScore / evidenceCount;
    const signatureCompleteness = signatureCompletenessScore / evidenceCount;
    const notesCompleteness = notesCompletenessScore / evidenceCount;
    
    // Weighted average: 50% photo, 30% signature, 20% notes
    const evidenceScore = (
      photoQuality * 0.5 +
      signatureCompleteness * 0.3 +
      notesCompleteness * 0.2
    );
    
    return evidenceScore;
  }
  
  /**
   * Get grade from score (0.0-1.0)
   */
  getGrade(score) {
    if (score >= 0.95) return { grade: 'A+', color: '#28a745', label: 'Excellent' };
    if (score >= 0.90) return { grade: 'A', color: '#28a745', label: 'Excellent' };
    if (score >= 0.85) return { grade: 'B+', color: '#5cb85c', label: 'Very Good' };
    if (score >= 0.80) return { grade: 'B', color: '#5cb85c', label: 'Good' };
    if (score >= 0.75) return { grade: 'C+', color: '#ffc107', label: 'Satisfactory' };
    if (score >= 0.70) return { grade: 'C', color: '#ffc107', label: 'Needs Improvement' };
    return { grade: 'F', color: '#dc3545', label: 'Unsatisfactory' };
  }
  
  /**
   * Get recommendations based on score breakdown
   */
  getRecommendations(timing, completeness, accuracy, evidence) {
    const recommendations = [];
    
    if (timing < 0.8) {
      recommendations.push({
        area: 'Timing',
        issue: 'Actions not completed within optimal timeframes',
        suggestion: 'Review protocol timing requirements and practice time management'
      });
    }
    
    if (completeness < 0.9) {
      recommendations.push({
        area: 'Completeness',
        issue: 'Some steps or actions were incomplete',
        suggestion: 'Ensure all required steps are completed before finishing task'
      });
    }
    
    if (accuracy < 0.9) {
      recommendations.push({
        area: 'Accuracy',
        issue: 'Sequence or validation issues detected',
        suggestion: 'Follow protocol sequence strictly and ensure all validations pass'
      });
    }
    
    if (evidence < 0.8) {
      recommendations.push({
        area: 'Evidence',
        issue: 'Missing or incomplete evidence capture',
        suggestion: 'Capture all required photos and signatures, add detailed notes'
      });
    }
    
    return recommendations;
  }
}

export const complianceService = new ComplianceService();



